// Expired-access-token handling (brainstorm-j1va). The access token lives 15
// minutes; if a request still reaches the server with an expired one it gets
// a plain 401. This interceptor refreshes the session once and retries the
// original request once with the new Bearer. Concurrent 401s share a single
// refresh. It deliberately ignores:
//   - billing's 401 {detail:{error:"reauth_required"}} (that is the step-up
//     modal's job, see StepUpModal.jsx), and
//   - /auth/* calls (a 401 there means "not signed in", not "expired token").

const isAuthCall = (config) => {
  const url = String(config?.url || '');
  return /(^|\/\/[^/]+)\/auth\//.test(url) || url.startsWith('auth/');
};

const isReauthRequired = (error) =>
  error?.response?.data?.detail?.error === 'reauth_required';

/**
 * refresh(): resolves with the new access token, or null if the session could
 * not be renewed; if it throws a 401, onAuthFailure() is called (sign out).
 * Returns an uninstall function.
 */
export function installRefreshInterceptor(instance, { refresh, onAuthFailure }) {
  let pending = null;
  const id = instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const config = error?.config;
      if (
        error?.response?.status !== 401 ||
        !config ||
        config.__refreshRetried ||
        isAuthCall(config) ||
        isReauthRequired(error)
      ) {
        return Promise.reject(error);
      }
      config.__refreshRetried = true; // never loops
      let newToken = null;
      try {
        if (!pending) {
          pending = Promise.resolve(refresh()).finally(() => { pending = null; });
        }
        newToken = await pending;
      } catch (e) {
        // The refresh itself was rejected: the session is gone, sign out.
        if (e?.response?.status === 401 && onAuthFailure) onAuthFailure();
        return Promise.reject(error);
      }
      // null: signed out (refreshSession already cleared) or a transient
      // failure (it schedules its own retry); either way keep the original error.
      if (!newToken) return Promise.reject(error);
      config.headers = config.headers || {};
      if (typeof config.headers.set === 'function') config.headers.set('Authorization', `Bearer ${newToken}`);
      else config.headers.Authorization = `Bearer ${newToken}`;
      return instance.request(config);
    }
  );
  return () => instance.interceptors.response.eject(id);
}
